// Canonical digests and the Execute/Close wire layout (program/src/digest.rs and
// the `ix` module of sdk/rust/src/lib.rs), byte for byte. Pinned by
// sdk/fixtures/digest-v1.json (test/digest.test.ts).

import { keccak_256 } from '@noble/hashes/sha3.js';
import { concat } from './derive.js';

export const EXEC_TAG: Uint8Array = new TextEncoder().encode('THEVAULT/EXEC/2');
export const CLOSE_TAG: Uint8Array = new TextEncoder().encode('THEVAULT/CLOSE/2');
export const FLAGS = { NEXT_KEY: 1, LAMPORT_MOVE: 2, SETTLE: 4, BOUNDED: 8, EXECUTOR: 16 } as const;
export const MAX_AUX = 16;
export const MAX_INNER_IX = 8;
export const MAX_INNER_ACCOUNTS = 64;
export const SIGNATURE_LEN = 1037;
export const PUBLIC_KEY_LEN = 41;
export const U64_MAX = 0xffffffffffffffffn;

export type DigestErrorCode = 'BadLength' | 'TooMany' | 'AuxMismatch' | 'UnknownRoute' | 'TooFewMetas' | 'InnerTooLarge' | 'BadSignatureLen';

export class DigestError extends Error {
  constructor(readonly code: DigestErrorCode, message: string) {
    super(message);
    this.name = 'DigestError';
  }
}

export interface SettleSpec {
  inIndex: number;
  outIndex: number;
  inMint: Uint8Array;
  outMint: Uint8Array;
  maxIn: bigint;
  minOut: bigint;
}

/** The Execute header as the client sees it; `headerBytes` is exactly what the program parses and hashes. */
export interface ExecHeaderSpec {
  numAux: number;
  validUntilSlot: bigint;
  executor?: Uint8Array;
  lamportMove?: { auxIndex: number; lamports: bigint };
  settle?: SettleSpec;
  nextKey?: Uint8Array;
  boundedIx?: number;
}

export interface AccountMetaSpec {
  pubkey: Uint8Array;
  isSigner: boolean;
  isWritable: boolean;
}

/** One inner instruction: program, metas in the callee's order (signer flags are scrubbed on the wire), data. */
export interface InnerIx {
  program: Uint8Array;
  metas: { pubkey: Uint8Array; isWritable: boolean }[];
  data: Uint8Array;
}

export const hex = (b: Uint8Array): string => Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
export const fromHex = (s: string): Uint8Array => Uint8Array.from(s.match(/../g) ?? [], (x) => parseInt(x, 16));

export function u8(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xff) throw new DigestError('BadLength', `u8 out of range: ${n}`);
  return Uint8Array.of(n);
}
export function u16le(n: number): Uint8Array {
  const out = new Uint8Array(2);
  new DataView(out.buffer).setUint16(0, n, true);
  return out;
}
export function u32le(n: number): Uint8Array {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, n >>> 0, true);
  return out;
}
export function u64le(n: bigint): Uint8Array {
  if (n < 0n || n > U64_MAX) throw new DigestError('BadLength', `u64 out of range: ${n}`);
  const out = new Uint8Array(8);
  new DataView(out.buffer).setBigUint64(0, n, true);
  return out;
}
function exact(b: Uint8Array, len: number, what: string): Uint8Array {
  if (b.length !== len) throw new DigestError('BadLength', `${what}: expected ${len} bytes, got ${b.length}`);
  return b;
}

export function headerFlags(h: ExecHeaderSpec): number {
  let f = 0;
  if (h.nextKey) f |= FLAGS.NEXT_KEY;
  if (h.lamportMove) f |= FLAGS.LAMPORT_MOVE;
  if (h.settle) f |= FLAGS.SETTLE;
  if (h.boundedIx !== undefined) f |= FLAGS.BOUNDED;
  if (h.executor) f |= FLAGS.EXECUTOR;
  return f;
}

/** flags || num_aux || valid_until_slot le || [executor] || [lamport_move] || [settle] || [next_key] || [bounded_ix]. */
export function headerBytes(h: ExecHeaderSpec): Uint8Array {
  const parts: Uint8Array[] = [u8(headerFlags(h)), u8(h.numAux), u64le(h.validUntilSlot)];
  if (h.executor) parts.push(exact(h.executor, 32, 'executor'));
  if (h.lamportMove) parts.push(u8(h.lamportMove.auxIndex), u64le(h.lamportMove.lamports));
  if (h.settle) {
    const s = h.settle;
    parts.push(u8(s.inIndex), u8(s.outIndex), exact(s.inMint, 32, 'in_mint'), exact(s.outMint, 32, 'out_mint'), u64le(s.maxIn), u64le(s.minOut));
  }
  if (h.nextKey) parts.push(exact(h.nextKey, PUBLIC_KEY_LEN, 'next_key'));
  if (h.boundedIx !== undefined) parts.push(u8(h.boundedIx));
  return concat(...parts);
}

/** EXACT block: keccak256(num_accounts u8 || program || metas... || data_len u16 le || data). */
export function innerBlockHash(program: Uint8Array, metas: Uint8Array[], data: Uint8Array): Uint8Array {
  if (metas.length > MAX_INNER_ACCOUNTS || data.length > 0xffff) throw new DigestError('InnerTooLarge', 'inner instruction too large');
  return keccak_256(concat(u8(metas.length), exact(program, 32, 'program'), ...metas.map((m) => exact(m, 32, 'meta')), u16le(data.length), data));
}

/** BOUNDED block: keccak256(0xFF || program || discriminator || authority || src || dst). */
export function boundedBlockHash(program: Uint8Array, discriminator: Uint8Array, authority: Uint8Array, src: Uint8Array, dst: Uint8Array): Uint8Array {
  return keccak_256(concat(u8(0xff), exact(program, 32, 'program'), exact(discriminator, 8, 'discriminator'), authority, src, dst));
}

export interface JupSlots {
  authority: number;
  src: number;
  dst: number;
}

/** Slot table of the allow-listed Jupiter v6 instructions (program/src/allowlist.rs), by discriminator. */
export const JUPITER_SLOTS: Readonly<Record<string, JupSlots>> = {
  e517cb977ae3ad2a: { authority: 1, src: 2, dst: 3 }, // route
  c1209b3341d69c81: { authority: 2, src: 3, dst: 6 }, // shared_accounts_route
  bb64facc31c4af14: { authority: 0, src: 1, dst: 2 }, // route_v2
  d19853937cfed8e9: { authority: 1, src: 2, dst: 5 }, // shared_accounts_route_v2
};

export function jupiterSlots(discriminator: Uint8Array): JupSlots | undefined {
  return JUPITER_SLOTS[hex(discriminator)];
}

export function blockHash(ix: InnerIx, bounded: boolean): Uint8Array {
  if (!bounded) return innerBlockHash(ix.program, ix.metas.map((m) => m.pubkey), ix.data);
  if (ix.data.length < 8) throw new DigestError('UnknownRoute', 'bounded instruction shorter than a discriminator');
  const disc = ix.data.subarray(0, 8);
  const s = jupiterSlots(disc);
  if (!s) throw new DigestError('UnknownRoute', `unknown Jupiter discriminator ${hex(disc)}`);
  const need = Math.max(s.authority, s.src, s.dst) + 1;
  if (ix.metas.length < need) throw new DigestError('TooFewMetas', `bounded route has ${ix.metas.length} metas, needs ${need}`);
  return boundedBlockHash(ix.program, disc, ix.metas[s.authority]!.pubkey, ix.metas[s.src]!.pubkey, ix.metas[s.dst]!.pubkey);
}

export interface ExecuteDigestInput {
  programId: Uint8Array;
  vaultId: Uint8Array;
  vaultAddress: Uint8Array;
  treeIndex: number;
  leaf: number;
  header: ExecHeaderSpec;
  /** Aux account addresses, in order. */
  aux: Uint8Array[];
  inners: InnerIx[];
}

/** keccak256(tag || program || vault_id || vault || tree le || leaf le || header || aux... || num_ix || blocks...). */
export function executeDigest(i: ExecuteDigestInput): Uint8Array {
  if (i.inners.length > MAX_INNER_IX || i.aux.length > MAX_AUX) throw new DigestError('TooMany', 'too many inner instructions or aux accounts');
  if (i.header.numAux !== i.aux.length) throw new DigestError('AuxMismatch', `header num_aux ${i.header.numAux} but ${i.aux.length} aux accounts`);
  const blocks = i.inners.map((ix, k) => blockHash(ix, i.header.boundedIx === k));
  return keccak_256(
    concat(
      EXEC_TAG,
      exact(i.programId, 32, 'program id'),
      exact(i.vaultId, 32, 'vault id'),
      exact(i.vaultAddress, 32, 'vault'),
      u32le(i.treeIndex),
      u32le(i.leaf),
      headerBytes(i.header),
      ...i.aux.map((a) => exact(a, 32, 'aux')),
      u8(i.inners.length),
      ...blocks,
    ),
  );
}

export interface CloseDigestInput {
  programId: Uint8Array;
  vaultId: Uint8Array;
  vaultAddress: Uint8Array;
  treeIndex: number;
  leaf: number;
  validUntilSlot: bigint;
  destination: Uint8Array;
}

export function closeDigest(i: CloseDigestInput): Uint8Array {
  return keccak_256(
    concat(CLOSE_TAG, exact(i.programId, 32, 'program id'), exact(i.vaultId, 32, 'vault id'), exact(i.vaultAddress, 32, 'vault'), u32le(i.treeIndex), u32le(i.leaf), u64le(i.validUntilSlot), exact(i.destination, 32, 'destination')),
  );
}

/** 0x01 || signature || header || num_ix || per inner: num_metas u8 || data_len u16 le || data. */
export function executeInstructionData(signature: Uint8Array, header: ExecHeaderSpec, inners: InnerIx[]): Uint8Array {
  if (signature.length !== SIGNATURE_LEN) throw new DigestError('BadSignatureLen', `signature is ${signature.length} bytes`);
  const parts = [u8(1), signature, headerBytes(header), u8(inners.length)];
  for (const ix of inners) parts.push(u8(ix.metas.length), u16le(ix.data.length), ix.data);
  return concat(...parts);
}

/** vault (writable), aux..., then per inner: its program (readonly) and its metas with is_signer scrubbed. */
export function executeAccounts(vault: Uint8Array, aux: AccountMetaSpec[], inners: InnerIx[]): AccountMetaSpec[] {
  const out: AccountMetaSpec[] = [{ pubkey: vault, isSigner: false, isWritable: true }, ...aux];
  for (const ix of inners) {
    out.push({ pubkey: ix.program, isSigner: false, isWritable: false });
    for (const m of ix.metas) out.push({ pubkey: m.pubkey, isSigner: false, isWritable: m.isWritable });
  }
  return out;
}

/** 0x02 || signature || valid_until_slot le. */
export function closeInstructionData(signature: Uint8Array, validUntilSlot: bigint): Uint8Array {
  if (signature.length !== SIGNATURE_LEN) throw new DigestError('BadSignatureLen', `signature is ${signature.length} bytes`);
  return concat(u8(2), signature, u64le(validUntilSlot));
}

/** Parse a header from the front of `data`: the spec and the number of bytes it occupied. */
export function parseHeader(data: Uint8Array): { header: ExecHeaderSpec; length: number } {
  const v = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const need = (n: number) => {
    if (o + n > data.length) throw new DigestError('BadLength', 'header truncated');
  };
  let o = 0;
  need(10);
  const flags = data[o++]!;
  if (flags & ~0x1f) throw new DigestError('BadLength', `unknown header flags ${flags}`);
  const numAux = data[o++]!;
  const validUntilSlot = v.getBigUint64(o, true);
  o += 8;
  const h: ExecHeaderSpec = { numAux, validUntilSlot };
  if (flags & FLAGS.EXECUTOR) {
    need(32);
    h.executor = data.slice(o, o + 32);
    o += 32;
  }
  if (flags & FLAGS.LAMPORT_MOVE) {
    need(9);
    h.lamportMove = { auxIndex: data[o]!, lamports: v.getBigUint64(o + 1, true) };
    o += 9;
  }
  if (flags & FLAGS.SETTLE) {
    need(82);
    h.settle = { inIndex: data[o]!, outIndex: data[o + 1]!, inMint: data.slice(o + 2, o + 34), outMint: data.slice(o + 34, o + 66), maxIn: v.getBigUint64(o + 66, true), minOut: v.getBigUint64(o + 74, true) };
    o += 82;
  }
  if (flags & FLAGS.NEXT_KEY) {
    need(PUBLIC_KEY_LEN);
    h.nextKey = data.slice(o, o + PUBLIC_KEY_LEN);
    o += PUBLIC_KEY_LEN;
  }
  if (flags & FLAGS.BOUNDED) {
    need(1);
    h.boundedIx = data[o++]!;
  }
  return { header: h, length: o };
}

/** Parse Execute instruction data and its account list back into signature, header, aux and inner instructions. */
export function parseExecute(data: Uint8Array, accounts: AccountMetaSpec[]): { signature: Uint8Array; header: ExecHeaderSpec; aux: AccountMetaSpec[]; inners: InnerIx[] } {
  if (data[0] !== 1 || data.length < 1 + SIGNATURE_LEN + 11) throw new DigestError('BadLength', 'not an Execute');
  const signature = data.slice(1, 1 + SIGNATURE_LEN);
  const { header, length } = parseHeader(data.subarray(1 + SIGNATURE_LEN));
  let o = 1 + SIGNATURE_LEN + length;
  const numIx = data[o++]!;
  const aux = accounts.slice(1, 1 + header.numAux);
  let a = 1 + header.numAux;
  const inners: InnerIx[] = [];
  for (let k = 0; k < numIx; k++) {
    const numMetas = data[o++]!;
    const len = data[o]! | (data[o + 1]! << 8);
    o += 2;
    const d = data.slice(o, o + len);
    o += len;
    const program = accounts[a++]?.pubkey;
    if (!program) throw new DigestError('BadLength', 'missing inner program account');
    const metas = accounts.slice(a, a + numMetas).map((m) => ({ pubkey: m.pubkey, isWritable: m.isWritable }));
    a += numMetas;
    inners.push({ program, metas, data: d });
  }
  if (o !== data.length || a !== accounts.length) throw new DigestError('BadLength', 'trailing bytes or accounts');
  return { signature, header, aux, inners };
}
